/* eslint-disable @typescript-eslint/no-inferrable-types */
import dayjs from "dayjs";
import "dayjs/locale/es";
import { HOSTNAMES, DEFAULT_HOST_VALUE, PERSON_CATALOG } from "./constantes";
import { BankAccount, PersonaStructure } from "@interfaces/response";
import { PersonalData } from "@interfaces/store";
import {
  BankAccountFormItem,
  MatrimonialRegimesData,
  CivilStatus,
  CheckListSignatureDocument,
} from "@interfaces/index";

dayjs.locale("es");

const meses = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const DEFAULT_INTEREST_RATE = 30;

export const capitalizeFirstLetter = (str: string) => {
  if (str.length === 0) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const removeSpecialCharacters = (value: string) => {
  return value.replace(/[^a-zA-ZáéíóúüÁÉÍÓÚÜñÑ\s']/g, "");
};

export const formatCurrency = (amount: number, currencySymbol = "$") => {
  if (!amount) return `${currencySymbol}0.00`;
  // Formatear el número con separadores de miles y dos decimales
  const formattedAmount = amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");

  // Retornar el string formateado con el símbolo de la moneda
  return `${currencySymbol}${formattedAmount}`;
};

export const amountToText = (amount: number, currencySymbol = ""): string => {
  amount = parseFloat(amount.toFixed(2));

  if (amount < 1000) {
    return `${currencySymbol}${amount}`;
  } else if (amount >= 1000 && amount < 1_000_000) {
    const thousands = Math.floor(amount / 1000);
    return `${currencySymbol}${thousands} mil`;
  } else if (amount >= 1_000_000 && amount < 1_000_000_000) {
    const millions = Math.floor(amount / 1_000_000);
    const residual = amount % 1_000_000;
    const thousands = Math.floor(residual / 1000);

    let result = `${currencySymbol}${millions} ${millions === 1 ? "millón" : "millones"}`;
    if (thousands % 1000 !== 0) {
      result += ` ${thousands} mil`;
    }
    return result;
  } else if (amount >= 1_000_000_000) {
    const billions = Math.floor(amount / 1_000_000_000);
    return `${currencySymbol}${billions} mil millones`;
  }

  return `${currencySymbol}${amount}`;
};

export const splitPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.length > 10) {
    const country_code = phoneNumber.slice(0, -10);
    const phone_number = phoneNumber.replace(/\D/g, "").slice(-10);
    return { country_code: country_code[0] === "+" ? country_code : `+${country_code}`, phone_number };
  } else {
    return { country_code: "", phone_number: phoneNumber };
  }
};

export const formatTime = (timeToFormat: number) => {
  const minutes = Math.floor(timeToFormat / 60);
  const seconds = timeToFormat % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return "";

  if (phoneNumber.length < 10) {
    return phoneNumber;
  }

  const part1 = phoneNumber.substring(0, 2);
  const part2 = phoneNumber.substring(2, 6);
  const part3 = phoneNumber.substring(6, 10);

  return `${part1}-${part2}-${part3}`;
};

// todo: delete or fix
export const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const convertSex = (sexString: string) => {
  if (!sexString) return "";
  return sexString.toLowerCase() === "masculino" ? "H" : "M";
};

export const extractRFCFromCurp = (curp: string) => {
  if (!curp || typeof curp !== "string" || curp.length < 10) {
    return "";
  }

  return curp.substring(0, 10);
};

export const calculateAmountMonthly = (
  loanAmount: number,
  loanTerm: number, // puede venir en días o en meses
  anualRate: number = DEFAULT_INTEREST_RATE,
) => {
  // si el plazo es mayor a 60 lo tratamos como días, si no, ya son meses
  const loanTermMonths = loanTerm > 60 ? Math.ceil(loanTerm / 30) : loanTerm;

  const monthlyRate = anualRate / 12 / 100;

  const payment = (loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -loanTermMonths));

  return Number(payment.toFixed(2));
};

export const obtenerFechaHoy = (): string => {
  const hoy = new Date();
  const dia = hoy.getDate();
  const mes = meses[hoy.getMonth()];
  const año = hoy.getFullYear();

  return `${dia} de ${mes} de ${año}`;
};

// Formatear la fecha a formato largo, Ej: 01 de enero de 2021
export const formatDateToLargeDate = (str_date: string) => {
  return dayjs(str_date).format(`DD [de] MMMM [de] YYYY`);
};

export const getMimeTypeFromFilename = (filename: string): string => {
  const extension = filename.split(".")[1];

  return (
    {
      aac: "audio/aac",
      abw: "application/x-abiword",
      arc: "application/x-freearc",
      avi: "video/x-msvideo",
      azw: "application/vnd.amazon.ebook",
      bin: "application/octet-stream",
      bmp: "image/bmp",
      bz: "application/x-bzip",
      bz2: "application/x-bzip2",
      cda: "application/x-cdf",
      csh: "application/x-csh",
      css: "text/css",
      csv: "text/csv",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      eot: "application/vnd.ms-fontobject",
      epub: "application/epub+zip",
      gz: "application/gzip",
      gif: "image/gif",
      htm: "text/html",
      html: "text/html",
      ico: "image/vnd.microsoft.icon",
      ics: "text/calendar",
      jar: "application/java-archive",
      jpeg: "image/jpeg",
      jpg: "image/jpeg",
      js: "text/javascript",
      json: "application/json",
      jsonld: "application/ld+json",
      mid: "audio/midi audio/x-midi",
      midi: "audio/midi audio/x-midi",
      mjs: "text/javascript",
      mp3: "audio/mpeg",
      mp4: "video/mp4",
      mpeg: "video/mpeg",
      mpkg: "application/vnd.apple.installer+xml",
      odp: "application/vnd.oasis.opendocument.presentation",
      ods: "application/vnd.oasis.opendocument.spreadsheet",
      odt: "application/vnd.oasis.opendocument.text",
      oga: "audio/ogg",
      ogv: "video/ogg",
      ogx: "application/ogg",
      opus: "audio/opus",
      otf: "font/otf",
      png: "image/png",
      pdf: "application/pdf",
      php: "application/x-httpd-php",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      rar: "application/vnd.rar",
      rtf: "application/rtf",
      sh: "application/x-sh",
      svg: "image/svg+xml",
      swf: "application/x-shockwave-flash",
      tar: "application/x-tar",
      tif: "image/tiff",
      tiff: "image/tiff",
      ts: "video/mp2t",
      ttf: "font/ttf",
      txt: "text/plain",
      vsd: "application/vnd.visio",
      wav: "audio/wav",
      weba: "audio/webm",
      webm: "video/webm",
      webp: "image/webp",
      woff: "font/woff",
      woff2: "font/woff2",
      xhtml: "application/xhtml+xml",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      xml: "application/xml",
      xul: "application/vnd.mozilla.xul+xml",
      zip: "application/zip",
      "3gp": "video/3gpp",
      "3g2": "video/3gpp2",
      "7z": "application/x-7z-compressed",
    }[extension] || "application/octet-stream"
  );
};

export const forceDownload = (url: string, filename: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getMonthName = (monthNumber: number) => {
  return meses[monthNumber - 1] || "";
};

export const transformTitleCase = (value: string) => {
  if (!value) return value;
  return removeSpecialCharacters(value);
};

export const getOrdinalText = (index: number) => {
  const ordinals = [
    "",
    "Primera",
    "Segunda",
    "Tercera",
    "Cuarta",
    "Quinta",
    "Sexta",
    "Séptima",
    "Octava",
    "Novena",
    "Décima",
  ];
  return ordinals[index] || "";
};

export const getShortOrdinal = (n: number): string => {
  const last_two = n % 100;
  const last_one = n % 10;

  let sufijo: string;

  if ([11, 12, 13].includes(last_two)) {
    sufijo = "mo";
  } else {
    switch (last_one) {
      case 1:
        sufijo = "er";
        break;
      case 2:
        sufijo = "do";
        break;
      case 3:
        sufijo = "er";
        break;
      case 4:
      case 5:
      case 6:
        sufijo = "to";
        break;
      case 7:
        sufijo = "mo";
        break;
      case 8:
        sufijo = "vo";
        break;
      case 9:
        sufijo = "no";
        break;
      case 0:
        sufijo = "mo";
        break;
      default:
        sufijo = "";
    }
  }

  return `${n}${sufijo}`;
};

export const separarNombres = (nombreCompleto: string) => {
  const partes = nombreCompleto.split(" ");

  let nombre = "";
  let apellido1 = "";
  let apellido2 = "";

  if (partes.length >= 2) {
    nombre = partes.slice(0, partes.length - 2).join(" ");
    apellido1 = partes[partes.length - 2];
    apellido2 = partes[partes.length - 1];
  } else if (partes.length === 1) {
    nombre = partes[0];
  }

  return {
    nombre,
    apellido1,
    apellido2,
  };
};

export const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength);
};

const images = import.meta.glob("/src/assets/img/**", { eager: true });

export const getDynamicUrl = (fileName: string): string | undefined => {
  const hostname = getBaseDomain(window.location.href);
  const foundHost = HOSTNAMES.find(({ host }) => host === hostname);
  const folder = foundHost?.value || "finbe";
  const path = `/src/assets/img/${folder}/${fileName}`;

  return (images as Record<string, { default: string }>)[path]?.default;
};

export const getBaseDomain = (url: string): string | null => {
  try {
    if (!/^https?:\/\//.test(url)) {
      url = "http://" + url;
    }

    const hostname = new URL(url).hostname;

    return hostname.replace(/^www\./, "");
  } catch (error) {
    return null;
  }
};

export const getConfigName = (url: string): string => {
  try {
    const hostname = getBaseDomain(url);
    const hostValue = HOSTNAMES.find(({ host }) => host === hostname)?.value;
    return hostValue || DEFAULT_HOST_VALUE;
  } catch (_error) {
    return DEFAULT_HOST_VALUE;
  }
};

export const getProportionalHeight = (img_width: number, img_height: number, new_width: number | string) => {
  const result = (img_height / img_width) * +new_width;
  return result;
};

export const capitalizeWords = (sentence: string) => {
  return sentence
    .toLowerCase()
    .split(" ")
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
};

export const identifyPersons = (
  persons: PersonaStructure[],
  list_civil_status?: CivilStatus[],
  list_matrimonial_regimen?: MatrimonialRegimesData[],
) => {
  const representantes: PersonaStructure[] = [];
  const avales: PersonaStructure[] = [];
  const conyuges: PersonaStructure[] = [];
  const propietarios: PersonaStructure[] = [];
  const segundos_avales: PersonaStructure[] = [];

  persons.forEach((personItem) => {
    const person_codes = personItem.person_types_detail.map((item) => item.code);
    if (person_codes.includes(PERSON_CATALOG.representante_legal.code)) {
      representantes.push(personItem);
    }
    if (person_codes.includes(PERSON_CATALOG.accionista_mayoritario.code)) {
      if (personItem.is_shareholder) {
        avales.push(personItem);
      }
    }
    if (person_codes.includes(PERSON_CATALOG.conyuge_aval.code)) {
      conyuges.push(personItem);
    }
    if (person_codes.includes(PERSON_CATALOG.propietario_real.code)) {
      if (personItem.is_shareholder) {
        propietarios.push(personItem);
      }
    }
    if (person_codes.includes(PERSON_CATALOG.segundo_aval.code)) {
      segundos_avales.push(personItem);
    }
  });

  // buscar el conyuge del aval
  const aval = avales.length > 0 ? avales[0] : undefined;
  let conyuge_aval = undefined;
  let aval_civil_status = undefined;
  let aval_matrimonial_regimen = undefined;

  if (aval) {
    const selected_civil_status = list_civil_status?.find((item) => item.id === aval.civil_status_id);

    aval_civil_status = selected_civil_status?.name.toLowerCase();

    if (aval_civil_status === "casado") {
      conyuge_aval = conyuges[0] || undefined;

      const selected_matrimonial_regimen = list_matrimonial_regimen?.find(
        (item) => item.id === aval.matrimonial_regimes_id,
      );

      if (selected_matrimonial_regimen) {
        aval_matrimonial_regimen = selected_matrimonial_regimen.code;
      }
    }
  }

  // transformar segundos_avales a objeto editable
  const segundos_avales_editable: PersonaStructure[] = segundos_avales.map((item) => ({
    ...item,
    personal_information_status:
      item.personal_information_status === "PENDIENTE" ? "INCIDENCIA" : item.personal_information_status,
    bank_references_status: item.bank_references_status === "PENDIENTE" ? "INCIDENCIA" : item.bank_references_status,
    identity_validation_completed:
      item.identity_validation_completed === null ? false : item.identity_validation_completed,
  }));

  return {
    representante: representantes.length > 0 ? representantes[0] : undefined,
    representantes,
    aval,
    conyuge_aval,
    propietarios,
    segundos_avales: segundos_avales_editable,
    aval_civil_status,
    aval_matrimonial_regimen,
    conyuges,
  };
};

export const formatCompanyList = (companies: string[], useA: boolean = false): string => {
  if (!companies || companies.length === 0) return "";

  const lastConnector = useA ? " y a " : " y ";

  if (companies.length === 1) return companies[0];
  if (companies.length === 2) return companies.join(lastConnector);

  const allExceptLast = companies.slice(0, -1).join(", ");
  const last = companies[companies.length - 1];
  return `${allExceptLast}${lastConnector}${last}`;
};

export const getAmplifyConfigByPath = (path: string): "client" | "guarantee" => {
  if (path.startsWith("/avales")) return "guarantee";
  return "client";
};

export const identifyBankAccounts = ({
  bank_accounts,
  company,
  aval,
}: {
  bank_accounts: BankAccount[];
  company?: PersonalData;
  aval?: PersonaStructure;
}) => {
  const active_accounts = bank_accounts.filter((item) => item.active === true);
  const company_accounts = active_accounts.filter((item) => item.client_details_id === company?.id);
  const aval_accounts = active_accounts.filter((item) => item.person_id === aval?.id);

  return {
    company_accounts,
    aval_accounts,
  };
};

export const createEmptyBankAccount = ({
  number_statements = 3,
}: {
  number_statements: number;
}): BankAccountFormItem => {
  const new_account: BankAccountFormItem = {
    id: "",
    clabe: "",
    bank_id: "",
    bank_name: "",
    statements: Array(number_statements).fill({
      bank_statement_document_id: undefined,
      document_id: undefined,
      document_order: undefined,
      document_value: undefined,
    }),
  };

  return new_account;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFormikArrayError = (formik: any, arrayName: string, index: number, inputName: string): string => {
  try {
    const { errors, touched } = formik;

    if (!errors || !touched) {
      return "";
    }

    if (!touched[arrayName] || !touched[arrayName][index] || !touched[arrayName][index][inputName]) {
      return "";
    }

    if (!errors[arrayName]) {
      return "";
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inputErrors = errors[arrayName] as any;
    if (!inputErrors || !inputErrors[index] || !inputErrors[index][inputName]) {
      return "";
    }

    return inputErrors[index][inputName] || "";
  } catch (error) {
    return "error_ocurrido " + (error as Error).message;
  }
};

export const validateStructureCheckListSignatureDocument = (item: CheckListSignatureDocument) => {
  if (item.title && typeof item.title === "string") {
    if (item.documents && item.documents.length > 0) {
      const are_all_strings = item.documents.every((document) => typeof document === "string");
      return are_all_strings;
    }
    return true;
  }
  return false;
};

export const getProductBaseRoute = (pathname: string) => {
  const parts = pathname.split("/").filter(Boolean);
  return `/${parts[0]}/${parts[1]}/`;
};

export const getPersonFullName = (person: PersonaStructure) => {
  return `${person.name || ""} ${person.last_name || ""} ${person.last_name_2 || ""}`.trim();
};

export const substractDateFromRFC = (rfc: string) => {
  const date_part = rfc.substring(4, 10);
  if (date_part.length < 6) {
    return null;
  }
  return dayjs(date_part, "YYMMDD").isValid() ? date_part : null;
};

export const substractDateFromCURP = (curp: string) => {
  const date_part = curp.substring(4, 10);
  if (date_part.length < 6) {
    return null;
  }
  return dayjs(date_part, "YYMMDD").isValid() ? date_part : null;
};

export const validateDates = (date_birth: string, date_part: string | null) => {
  if (!date_part) return false;
  const date_shareholder = dayjs(date_birth, "YYYY-MM-DD").format("YYMMDD");
  return date_shareholder === date_part;
};

/**
 * Returns the base URL (protocol + host) in the form "https://my-site.com" or
 * "https://my-site.com.mx". Strips the "www." subdomain when present.
 */
export const getBaseUrl = (url: string) => {
  let normalizedUrl = url.trim();

  if (!/^https?:\/\//i.test(normalizedUrl)) {
    normalizedUrl = "https://" + normalizedUrl;
  }

  try {
    const parsed = new URL(normalizedUrl);
    const host = parsed.hostname.toLowerCase().startsWith("www.") ? parsed.hostname.slice(4) : parsed.hostname;
    const port = parsed.port && parsed.port !== "80" && parsed.port !== "443" ? `:${parsed.port}` : "";
    return `${parsed.protocol}//${host}${port}`;
  } catch (e) {
    throw new Error(`URL inválida: ${url}`);
  }
};
